import { foldForSearch } from "./search";
import { type CompanyClassSummary, type EmployerGroup, type EmployerRef } from "./types/company-classes";

/** Mirrors the backend (`companies/services.py`). */
export const FALLBACK_CLASS_NAME = "Sonstige";
export const MIN_COMPANIES_PER_CLASS = 5;

export type ClassPickerOption =
  | { kind: "class" | "fallback"; key: string; label: string; classId: number }
  | { kind: "create"; key: string; label: string; name: string };

const collapse = (text: string): string => text.trim().replace(/\s+/g, " ");
const sameName = (a: string, b: string): boolean => foldForSearch(collapse(a)) === foldForSearch(collapse(b));

/** The options of a row's class picker for the typed text: „Als Sonstige
 * markieren" first, then the matching classes alphabetically, then „Neue
 * Klasse „…" anlegen" when the text names no existing class. `currentClassId`
 * (the rows' own class) is left out. */
export function classPickerOptions(
  classes: readonly CompanyClassSummary[],
  query: string,
  currentClassId?: number | null,
): ClassPickerOption[] {
  const needle = foldForSearch(collapse(query));
  const matches = (name: string): boolean => foldForSearch(name).includes(needle);
  const fallback = classes.find((c) => c.isFallback);
  const options: ClassPickerOption[] = [];
  if (fallback && fallback.id !== currentClassId && matches(FALLBACK_CLASS_NAME)) {
    options.push({ kind: "fallback", key: `class-${fallback.id}`, label: "Als Sonstige markieren", classId: fallback.id });
  }
  classes
    .filter((c) => !c.isFallback && c.id !== currentClassId && matches(c.name))
    .sort((a, b) => a.name.localeCompare(b.name, "de"))
    .forEach((c) => options.push({ kind: "class", key: `class-${c.id}`, label: c.name, classId: c.id }));
  const typed = collapse(query);
  if (typed && !classes.some((c) => sameName(c.name, typed)) && !sameName(FALLBACK_CLASS_NAME, typed)) {
    options.push({ kind: "create", key: "create", label: `Neue Klasse „${typed}“ anlegen`, name: typed });
  }
  return options;
}

/** Why a class name can't be used, or null. `ownId` is the class being renamed. */
export function classNameError(
  name: string,
  classes: readonly CompanyClassSummary[],
  ownId?: number,
): string | null {
  const typed = collapse(name);
  if (!typed) return "Bitte einen Namen eingeben.";
  if (sameName(FALLBACK_CLASS_NAME, typed)) return `„${FALLBACK_CLASS_NAME}“ ist reserviert.`;
  if (classes.some((c) => c.id !== ownId && sameName(c.name, typed))) return `Eine Klasse „${typed}“ gibt es schon.`;
  return null;
}

/** „zu klein · 3 von 5" for a class that can't hide an employer yet. */
export function classSizeLabel(summary: CompanyClassSummary): string | null {
  return summary.tooSmall ? `zu klein · ${summary.companyCount} von ${MIN_COMPANIES_PER_CLASS}` : null;
}

export function employerRef(group: EmployerGroup): EmployerRef {
  return group.companyId !== null ? { companyId: group.companyId } : { key: group.key ?? "", name: group.name };
}

/** Undoing a bulk assignment: one request per class the rows were in before. */
export function undoBatches(rows: readonly EmployerGroup[]): { classId: number | null; items: EmployerRef[] }[] {
  const batches = new Map<number | null, EmployerRef[]>();
  for (const row of rows) {
    const items = batches.get(row.companyClassId) ?? [];
    items.push(employerRef(row));
    batches.set(row.companyClassId, items);
  }
  return [...batches].map(([classId, items]) => ({ classId, items }));
}
